import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import { AlertState, ClientState, SettingState } from "../../types/user";
import { SuccessPayment } from "types/payment";
import { isMobile } from "config/globalVariables";
import { localStorageKeys } from "config/localStorageKeys";

const getInitialSettings = (): Partial<SettingState> => {
  try {
    const storedSettings = localStorage.getItem(localStorageKeys.userPreferences);
    if (storedSettings) {
      return JSON.parse(storedSettings);
    }
  } catch (error) {
    console.error("Error parsing user preferences from localStorage", error);
  }
  return {
    typeChooseCardBidding: "block",
    typeChooseCard: "block",
    fonColor: "blue",
    sound: true,
    shuffle: false
  };
};

export type SubscriptionState = {
  client: Partial<ClientState>;
  isAuth: boolean;
  isLoading: boolean;
  settings: Partial<SettingState>;
  redirect_after_auth: string;
  showAlert: AlertState;
  successDataPayment: Partial<SuccessPayment>;
  windowSize: number;
  isMobileVersion: boolean;
  shuffleBlock: boolean;
  blockMixCourses: boolean;
};

export const initialState: SubscriptionState = {
  isAuth: false,
  client: {},
  isLoading: false,
  redirect_after_auth: "",
  showAlert: {
    show: false
  },
  settings: getInitialSettings(),
  successDataPayment: {},
  windowSize: window.innerWidth,
  isMobileVersion: isMobile,
  shuffleBlock: false,
  blockMixCourses: false
};

const coreSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    resetUser() {
      return initialState;
    },
    updateUser(state, action: PayloadAction<Partial<ClientState>>) {
      state.client = { ...state.client, ...action.payload };
      return state;
    },
    setUser(state, action: PayloadAction<Partial<ClientState>>) {
      state.client = { ...action.payload };
      return state;
    },
    setSimpleField(
      state,
      action: PayloadAction<{
        type: "isAuth" | "isLoading" | "shuffleBlock" | "blockMixCourses";
        value: any;
      }>
    ) {
      state[action.payload.type] = action.payload.value;
      return state;
    },
    setSettings(state, action: PayloadAction<SettingState>) {
      state.settings = action.payload;
      return state;
    },
    updateSettings(state, action: PayloadAction<Partial<SettingState>>) {
      state.settings = {
        ...state.settings,
        ...action.payload
      };
      return state;
    },
    setRedirectAfterAuth(state, action: PayloadAction<Partial<string>>) {
      state.redirect_after_auth = action.payload;
      return state;
    },
    setTriggerAlert(state, action: PayloadAction<AlertState>) {
      state.showAlert = action.payload;
      return state;
    },
    setSuccesDataPayment(state, action: PayloadAction<SuccessPayment>) {
      state.successDataPayment = action.payload;
      return state;
    },
    setWindowSize(state, action: PayloadAction<number>) {
      state.windowSize = action.payload;
      state.isMobileVersion = isMobile;
      return state;
    }
  }
});

export const {
  updateUser,
  resetUser,
  setSimpleField,
  setUser,
  setSettings,
  updateSettings,
  setRedirectAfterAuth,
  setTriggerAlert,
  setSuccesDataPayment,
  setWindowSize
} = coreSlice.actions;

export default coreSlice.reducer;
